var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getBaseUrl } from '../../base/util/helpers';
import logger from './logger';
const krispState = {
    filterNode: undefined,
    filterNodeReady: false,
    sdk: undefined,
    sdkInitialized: false
};
const loadDynamicModule = (modulePath) => {
    const dynamicImport = new Function('path', 'return import(/* webpackIgnore: true */ path);');
    return dynamicImport(modulePath);
};
let audioContext;
/**
 * Class Implementing the effect interface expected by a MHLocalTrack.
 * Effect applies rnnoise denoising on a audio MHLocalTrack.
 */
export class NoiseSuppressionEffect {
    /**
     * Instantiates a noise suppressor audio effect which will use either rnnoise or krisp.
     *
     * @param {INoiseSuppressionConfig} options - Configured options.
     */
    constructor(options) {
        var _a;
        this._options = options;
        const useKrisp = (_a = options === null || options === void 0 ? void 0 : options.krisp) === null || _a === void 0 ? void 0 : _a.enabled;
        logger.info(`NoiseSuppressionEffect created with ${useKrisp ? 'Krisp' : 'RNNoise'}`);
    }
    /**
     * Effect interface called by source MHLocalTrack.
     * Applies effect that uses a {@code NoiseSuppressor} service initialized with {@code RnnoiseProcessor}
     * for denoising.
     *
     * @param {MediaStream} audioStream - Audio stream which will be mixed with _mixAudio.
     * @returns {MediaStream} - MediaStream containing both audio tracks mixed together.
     */
    startEffect(audioStream) {
        var _a, _b;
        this._originalMediaTrack = audioStream.getAudioTracks()[0];
        if (!audioContext) {
            audioContext = new AudioContext();
        }
        this._audioSource = audioContext.createMediaStreamSource(audioStream);
        this._audioDestination = audioContext.createMediaStreamDestination();
        this._outputMediaTrack = this._audioDestination.stream.getAudioTracks()[0];
        let init;
        if ((_b = (_a = this._options) === null || _a === void 0 ? void 0 : _a.krisp) === null || _b === void 0 ? void 0 : _b.enabled) {
            init = _initializeKrisp(this._options, audioStream).then(filterNode => {
                var _a;
                this._noiseSuppressorNode = filterNode;
                if (krispState.filterNodeReady) {
                    (_a = krispState.filterNode) === null || _a === void 0 ? void 0 : _a.enable();
                }
            });
        }
        else {
            init = _initializeKRnnoise().then(filterNode => {
                this._noiseSuppressorNode = filterNode;
            });
        }
        // Connect the audio processing graph MediaStream -> AudioWorkletNode -> MediaStreamAudioDestinationNode
        init.then(() => {
            if (this._noiseSuppressorNode) {
                this._audioSource.connect(this._noiseSuppressorNode);
                this._noiseSuppressorNode.connect(this._audioDestination);
            }
        });
        // Sync the effect track muted state with the original track state.
        this._outputMediaTrack.enabled = this._originalMediaTrack.enabled;
        // We enable the audio on the original track because mute/unmute action will only affect the audio destination
        // output track from this point on.
        this._originalMediaTrack.enabled = true;
        return this._audioDestination.stream;
    }
    /**
     * Checks if the MHLocalTrack supports this effect.
     *
     * @param {MHLocalTrack} sourceLocalTrack - Track to which the effect will be applied.
     * @returns {boolean} - Returns true if this effect can run on the specified track, false otherwise.
     */
    isEnabled(sourceLocalTrack) {
        // MHLocalTracks needs to be an audio track.
        return sourceLocalTrack.isAudioTrack();
    }
    /**
     * Clean up resources acquired by noise suppressor and rnnoise processor.
     *
     * @returns {void}
     */
    stopEffect() {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        // Sync original track muted state with effect state before removing the effect.
        this._originalMediaTrack.enabled = this._outputMediaTrack.enabled;
        if ((_b = (_a = this._options) === null || _a === void 0 ? void 0 : _a.krisp) === null || _b === void 0 ? void 0 : _b.enabled) {
            // When using Krisp we'll just disable the filter which we'll keep reusing.
            (_c = this._noiseSuppressorNode) === null || _c === void 0 ? void 0 : _c.disable();
        }
        else {
            // Technically after this process the Audio Worklet along with it's resources should be garbage collected,
            // however on chrome there seems to be a problem as described here:
            // https://bugs.chromium.org/p/chromium/issues/detail?id=1298955
            (_e = (_d = this._noiseSuppressorNode) === null || _d === void 0 ? void 0 : _d.port) === null || _e === void 0 ? void 0 : _e.close();
        }
        (_f = this._audioDestination) === null || _f === void 0 ? void 0 : _f.disconnect();
        (_g = this._noiseSuppressorNode) === null || _g === void 0 ? void 0 : _g.disconnect();
        (_h = this._audioSource) === null || _h === void 0 ? void 0 : _h.disconnect();
        // Do NOT call `audioContext.suspend()` here. `audioContext` is a module-level singleton shared by every
        // NoiseSuppressionEffect instance (and, on this codebase, any future effect that picks the same
        // AudioContext). Suspending it would tear down audio processing for the whole conference - the original
        // mic, the screenshare audio mix, every active effect - because every consumer routes through the same
        // context. The previous implementation suspended the context on every `stopEffect()`, which is exactly the
        // mechanism behind the reported "noise cancellation dynamically gets turned off and the user's voice is not
        // being heard": a device change, a setEffect re-apply, or even a single effect re-creation triggered the
        // suspend and silenced everything downstream. Per-instance cleanup (disconnect + close the worklet port) is
        // sufficient for garbage collection; the context itself stays running.
    }
}
/**
 * Initializes the Krisp SDK and creates the filter node.
 *
 * @param {INoiseSuppressionConfig} options - Krisp options.
 * @param {MediaStream} stream - Audio stream which will be mixed with _mixAudio.
 *
 * @returns {Promise<AudioWorkletNode | undefined>}
 */
function _initializeKrisp(options, stream) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4;
        yield audioContext.resume();
        if (!krispState.sdk) {
            const baseUrl = `${getBaseUrl()}libs/krisp`;
            const { default: KrispSDK } = yield loadDynamicModule(`${baseUrl}/krispsdk.mjs`);
            const ncParams = {
                krisp: {
                    models: {
                        modelBVC: `${baseUrl}/models/${(_b = (_a = options === null || options === void 0 ? void 0 : options.krisp) === null || _a === void 0 ? void 0 : _a.models) === null || _b === void 0 ? void 0 : _b.modelBVC}`,
                        model8: `${baseUrl}/models/${(_d = (_c = options === null || options === void 0 ? void 0 : options.krisp) === null || _c === void 0 ? void 0 : _c.models) === null || _d === void 0 ? void 0 : _d.model8}`,
                        modelNC: `${baseUrl}/models/${(_f = (_e = options === null || options === void 0 ? void 0 : options.krisp) === null || _e === void 0 ? void 0 : _e.models) === null || _f === void 0 ? void 0 : _f.modelNC}`
                    },
                    logProcessStats: !((_g = options === null || options === void 0 ? void 0 : options.krisp) === null || _g === void 0 ? void 0 : _g.logProcessStats),
                    debugLogs: !((_h = options === null || options === void 0 ? void 0 : options.krisp) === null || _h === void 0 ? void 0 : _h.debugLogs),
                    useBVC: !((_j = options === null || options === void 0 ? void 0 : options.krisp) === null || _j === void 0 ? void 0 : _j.useBVC),
                    bvc: {
                        allowedDevices: `${baseUrl}/assets/${(_l = (_k = options === null || options === void 0 ? void 0 : options.krisp) === null || _k === void 0 ? void 0 : _k.bvc) === null || _l === void 0 ? void 0 : _l.allowedDevices}`,
                        allowedDevicesExt: `${baseUrl}/assets/${(_o = (_m = options === null || options === void 0 ? void 0 : options.krisp) === null || _m === void 0 ? void 0 : _m.bvc) === null || _o === void 0 ? void 0 : _o.allowedDevicesExt}`
                    },
                    inboundModels: {
                        modelInbound8: `${baseUrl}/models/${(_q = (_p = options === null || options === void 0 ? void 0 : options.krisp) === null || _p === void 0 ? void 0 : _p.inboundModels) === null || _q === void 0 ? void 0 : _q.modelInbound8}`,
                        modelInbound16: `${baseUrl}/models/${(_s = (_r = options === null || options === void 0 ? void 0 : options.krisp) === null || _r === void 0 ? void 0 : _r.inboundModels) === null || _s === void 0 ? void 0 : _s.modelInbound16}`
                    },
                    preloadModels: {
                        modelBVC: `${baseUrl}/models/${(_u = (_t = options === null || options === void 0 ? void 0 : options.krisp) === null || _t === void 0 ? void 0 : _t.preloadModels) === null || _u === void 0 ? void 0 : _u.modelBVC}`,
                        model8: `${baseUrl}/models/${(_w = (_v = options === null || options === void 0 ? void 0 : options.krisp) === null || _v === void 0 ? void 0 : _v.preloadModels) === null || _w === void 0 ? void 0 : _w.model8}`,
                        modelNC: `${baseUrl}/models/${(_y = (_x = options === null || options === void 0 ? void 0 : options.krisp) === null || _x === void 0 ? void 0 : _x.preloadModels) === null || _y === void 0 ? void 0 : _y.modelNC}`
                    },
                    preloadInboundModels: {
                        modelInbound8: `${baseUrl}/models/${(_0 = (_z = options === null || options === void 0 ? void 0 : options.krisp) === null || _z === void 0 ? void 0 : _z.preloadInboundModels) === null || _0 === void 0 ? void 0 : _0.modelInbound8}`,
                        modelInbound16: `${baseUrl}/models/${(_2 = (_1 = options === null || options === void 0 ? void 0 : options.krisp) === null || _1 === void 0 ? void 0 : _1.preloadInboundModels) === null || _2 === void 0 ? void 0 : _2.modelInbound16}`
                    }
                }
            };
            krispState.sdk = new KrispSDK({
                params: ncParams.krisp,
                callbacks: {}
            });
        }
        if (!krispState.sdkInitialized) {
            yield ((_3 = krispState.sdk) === null || _3 === void 0 ? void 0 : _3.init());
            krispState.sdkInitialized = true;
        }
        if (!krispState.filterNode) {
            try {
                krispState.filterNode = yield ((_4 = krispState.sdk) === null || _4 === void 0 ? void 0 : _4.createNoiseFilter({
                    audioContext,
                    stream
                }, () => {
                    var _a;
                    logger.info('Krisp audio filter ready');
                    // Enable audio filtering.
                    (_a = krispState.filterNode) === null || _a === void 0 ? void 0 : _a.enable();
                    krispState.filterNodeReady = true;
                }));
            }
            catch (e) {
                logger.error('Failed to create Krisp noise filter', e);
                krispState.filterNode = undefined;
                krispState.filterNodeReady = false;
            }
        }
        return krispState.filterNode;
    });
}
/**
 * Initializes the RNNoise audio worklet and creates the filter node.
 *
 * @returns {Promise<AudioWorkletNode | undefined>}
 */
function _initializeKRnnoise() {
    return __awaiter(this, void 0, void 0, function* () {
        yield audioContext.resume();
        const baseUrl = `${getBaseUrl()}libs/`;
        const workletUrl = `${baseUrl}noise-suppressor-worklet.min.js`;
        try {
            yield audioContext.audioWorklet.addModule(workletUrl);
        }
        catch (e) {
            logger.error('Error while adding audio worklet module: ', e);
            return;
        }
        // After the resolution of module loading, an AudioWorkletNode can be constructed.
        return new AudioWorkletNode(audioContext, 'NoiseSuppressorWorklet');
    });
}
